import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  AppBar,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  useMediaQuery,
  Typography,
  Avatar,
  Tooltip,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import BookIcon from '@mui/icons-material/Book';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AdminDashboard from './pages/AdminDashboard';
import Home from './pages/Home';
import Bookings from './pages/Bookings';
import BookingDetails from './pages/BookingDetails';
import DeliveryChecklistPage from './pages/DeliveryChecklist';
import Quotations from './pages/Quotations';
import QuotationDetails from './pages/QuotationDetails';
import Customers from './pages/Customers';
import CustomerDetails from './pages/CustomerDetails';
import ProductDetails from './pages/ProductDetails';
import GCalSettings from './pages/GCalSettings';
import LoginPage from './components/LoginPage';
import { useUser } from './components/UserContext';
import ProtectedRoute from './components/ProtectedRoute';
import UserManagement from './components/UserManagement';
import MobileBottomNav from './components/ui/MobileBottomNav';
import { layout, shadows, sidebarGradient, sidebarAccent } from './styles/tokens';

const MOBILE_DRAWER_WIDTH = 'min(280px, 85vw)';
const BOTTOM_NAV_PATHS = ['/', '/products', '/bookings', '/customers'];

const getPrimaryBottomNavItems = (items) => {
  const primary = BOTTOM_NAV_PATHS.map((path) => items.find((item) => item.path === path)).filter(Boolean);
  if (primary.length >= 4) return primary.slice(0, 4);
  const used = new Set(primary.map((i) => i.path));
  return [...primary, ...items.filter((i) => !used.has(i.path))].slice(0, 4);
};

const getPageTitle = (pathname, navItems) => {
  const match = navItems.find(
    (item) => pathname === item.path || (item.path !== '/' && pathname.startsWith(item.path))
  );
  if (match) return match.text;
  if (pathname.startsWith('/bookings/')) return 'Booking';
  if (pathname.startsWith('/customers/')) return 'Customer';
  if (pathname.startsWith('/products/')) return 'Product';
  if (pathname.startsWith('/quotations/')) return 'Quotation';
  return 'E One Rentals';
};

const navItems = [
  { text: 'Dashboard', shortLabel: 'Home', Icon: DashboardIcon, path: '/', allowedRoles: ['admin', 'front_office', 'accounts'] },
  { text: 'Products', shortLabel: 'Products', Icon: HomeIcon, path: '/products', allowedRoles: ['admin', 'front_office', 'warehouse'] },
  { text: 'Bookings', shortLabel: 'Bookings', Icon: BookIcon, path: '/bookings', allowedRoles: ['admin', 'front_office', 'warehouse', 'accounts'] },
  { text: 'Quotations', shortLabel: 'Quotes', Icon: DescriptionIcon, path: '/quotations', allowedRoles: ['admin', 'front_office', 'accounts'] },
  { text: 'Customers', shortLabel: 'Customers', Icon: PeopleIcon, path: '/customers', allowedRoles: ['admin', 'front_office'] },
  { text: 'G-Calendar', shortLabel: 'Calendar', Icon: CalendarTodayIcon, path: '/gcal-settings', allowedRoles: ['admin', 'front_office'] },
];

const App = () => {
  const theme = useTheme();
  const sidebar = theme.palette.sidebar;
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  let navItemsWithUser = navItems;
  if (user && user.role === 'admin') {
    navItemsWithUser = [...navItems, { text: 'Team', shortLabel: 'Team', Icon: PeopleIcon, path: '/users', allowedRoles: ['admin'] }];
  }
  if (user) {
    const role = (user.role || '').toLowerCase();
    navItemsWithUser = navItemsWithUser.filter((item) =>
      item.allowedRoles?.some((r) => String(r).toLowerCase() === role)
    );
  }

  const userInitial = (user?.username || 'A').charAt(0).toUpperCase();
  const roleLabel = (user?.role || 'admin').replace(/_/g, ' ');
  const primaryBottomNavItems = getPrimaryBottomNavItems(navItemsWithUser);
  const moreMenuItems = navItemsWithUser.filter(
    (item) => !primaryBottomNavItems.some((p) => p.path === item.path)
  );
  const mobilePageTitle = getPageTitle(location.pathname, navItemsWithUser);

  const drawerNavItems = isMobile ? moreMenuItems : navItemsWithUser;

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: sidebarGradient,
        color: sidebar.text,
      }}
    >
      <Box sx={{ px: 2.5, py: 3, borderBottom: `1px solid ${sidebar.border}`, position: 'relative' }}>
        {isMobile && (
          <IconButton
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            sx={{
              position: 'absolute',
              top: 12,
              right: 8,
              color: sidebar.text,
              '&:hover': { bgcolor: sidebar.hover, color: sidebar.textActive },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: isMobile ? 5 : 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              bgcolor: 'rgba(90, 103, 216, 0.2)',
              border: '1px solid rgba(90, 103, 216, 0.35)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1.125rem',
            }}
          >
            E
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', color: sidebar.textActive, lineHeight: 1.35, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              E One Rentals
            </Typography>
            <Typography sx={{ fontSize: '0.625rem', color: sidebar.textMuted, mt: 0.25, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 500 }}>
              Enterprise Portal
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 1.5, py: 2, flex: 1, overflow: 'auto' }}>
        <Typography
          sx={{
            px: 1.5,
            mb: 1,
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: sidebar.textMuted,
          }}
        >
          {isMobile ? 'More options' : 'Main menu'}
        </Typography>
        <List sx={{ p: 0, '& .MuiListItemButton-root': { mb: 0.25, py: 1.25, borderRadius: 2 } }}>
          {drawerNavItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <ListItemButton
                key={item.text}
                component={Link}
                to={item.path}
                selected={isActive}
                onClick={() => isMobile && setMobileOpen(false)}
                sx={{
                  position: 'relative',
                  color: isActive ? sidebar.textActive : sidebar.text,
                  '&:hover': { bgcolor: sidebar.hover, color: sidebar.textActive },
                  '&.Mui-selected': {
                    bgcolor: sidebar.active,
                    color: sidebar.textActive,
                    borderLeft: `3px solid ${sidebarAccent}`,
                    pl: 'calc(16px - 3px)',
                    '&:hover': { bgcolor: sidebar.active },
                    '& .nav-icon-box': {
                      bgcolor: 'rgba(90, 103, 216, 0.22)',
                      color: sidebar.textActive,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                  <Box
                    className="nav-icon-box"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isActive ? 'transparent' : 'rgba(255,255,255,0.04)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <item.Icon sx={{ fontSize: 20 }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: isActive ? 600 : 500 }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      <Box sx={{ p: 2, borderTop: `1px solid ${sidebar.border}` }}>
        {user && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              mb: 1.5,
              px: 1,
              py: 1,
              borderRadius: 2,
              bgcolor: sidebar.bgElevated,
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'rgba(90, 103, 216, 0.25)',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: 600,
                border: '1px solid rgba(90, 103, 216, 0.3)',
              }}
            >
              {userInitial}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: sidebar.textActive }}>
                {user.username}
              </Typography>
              <Typography noWrap sx={{ fontSize: '0.6875rem', color: sidebar.text, textTransform: 'capitalize' }}>
                {roleLabel}
              </Typography>
            </Box>
          </Box>
        )}
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: sidebar.text,
            '&:hover': { bgcolor: sidebar.hover, color: 'rgba(252, 165, 165, 0.9)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>
            <LogoutIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: 500 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  const mainRoutes = (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<ProtectedRoute component={AdminDashboard} allowedRoles={['admin', 'front_office', 'accounts']} />} />
      <Route path="/products" element={<ProtectedRoute component={Home} allowedRoles={['admin', 'front_office', 'warehouse']} />} />
      <Route path="/bookings" element={<ProtectedRoute component={Bookings} allowedRoles={['admin', 'front_office', 'warehouse', 'accounts']} />} />
      <Route path="/bookings/:id" element={<ProtectedRoute component={BookingDetails} allowedRoles={['admin', 'front_office', 'warehouse', 'accounts']} />} />
      <Route path="/bookings/:id/checklist" element={<ProtectedRoute component={DeliveryChecklistPage} allowedRoles={['admin', 'front_office', 'warehouse', 'accounts']} />} />
      <Route path="/quotations" element={<ProtectedRoute component={Quotations} allowedRoles={['admin', 'front_office', 'accounts']} />} />
      <Route path="/quotations/:id" element={<ProtectedRoute component={QuotationDetails} allowedRoles={['admin', 'front_office', 'accounts']} />} />
      <Route path="/customers" element={<ProtectedRoute component={Customers} allowedRoles={['admin', 'front_office']} />} />
      <Route path="/customers/:id" element={<ProtectedRoute component={CustomerDetails} allowedRoles={['admin', 'front_office']} />} />
      <Route path="/products/:id" element={<ProtectedRoute component={ProductDetails} allowedRoles={['admin', 'front_office', 'warehouse']} />} />
      <Route path="/users" element={<ProtectedRoute component={UserManagement} allowedRoles={['admin']} />} />
      <Route path="/gcal-settings" element={<ProtectedRoute component={GCalSettings} allowedRoles={['admin', 'front_office']} />} />
      <Route path="*" element={<Navigate to={user ? '/' : '/login'} replace />} />
    </Routes>
  );

  if (location.pathname === '/login') {
    return <Box sx={{ width: '100%', minHeight: '100vh', overflowX: 'hidden' }}>{mainRoutes}</Box>;
  }

  if (!user) {
    return mainRoutes;
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            zIndex: (t) => t.zIndex.appBar,
            bgcolor: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(12px)',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 2, minHeight: 56 }}>
            <Typography noWrap sx={{ fontSize: '1.0625rem', fontWeight: 600, flex: 1 }}>
              {mobilePageTitle}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setMobileOpen(true)}
              aria-label="Account and more"
              sx={{ ml: 1 }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8125rem', fontWeight: 600 }}>
                {userInitial}
              </Avatar>
            </IconButton>
          </Box>
        </AppBar>
      )}

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={
          isMobile
            ? {
                keepMounted: true,
                sx: { zIndex: (t) => t.zIndex.modal },
              }
            : undefined
        }
        sx={{
          width: isMobile ? MOBILE_DRAWER_WIDTH : layout.sidebarWidth,
          flexShrink: 0,
          ...(isMobile && { zIndex: (t) => t.zIndex.modal }),
          '& .MuiDrawer-paper': {
            width: isMobile ? MOBILE_DRAWER_WIDTH : layout.sidebarWidth,
            boxSizing: 'border-box',
            border: 'none',
            top: 0,
            height: '100%',
            background: sidebarGradient,
            ...(isMobile && {
              boxShadow: shadows.sidebar,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minWidth: 0 }}>
        <AppBar
          position="sticky"
          sx={{
            display: { xs: 'none', lg: 'flex' },
            bgcolor: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(12px)',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'rgba(90, 103, 216, 0.1)',
            boxShadow: shadows.header,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              minHeight: 64,
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>
                E One Rentals
              </Typography>
              <Typography sx={{ fontSize: '0.9375rem', color: 'text.disabled' }}>/</Typography>
              <Typography sx={{ fontSize: '0.9375rem', color: 'text.secondary', fontWeight: 500 }}>
                Inventory Management
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title="Notifications">
                <IconButton
                  size="small"
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    position: 'relative',
                  }}
                >
                  <NotificationsNoneIcon sx={{ fontSize: 20 }} />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      border: '2px solid #fff',
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.875rem', fontWeight: 600 }}>
                  {userInitial}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.2 }}>
                    {user.username}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', textTransform: 'capitalize' }}>
                    {roleLabel}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </AppBar>

        <Box
          component="main"
          sx={{
            flex: 1,
            overflow: 'auto',
            pt: { xs: 8, lg: 3 },
            px: { xs: 2, sm: 2.5, lg: 3 },
            pb: {
              xs: `calc(${layout.mobileBottomNavHeight}px + env(safe-area-inset-bottom, 0px) + 16px)`,
              lg: 3,
            },
            background: 'radial-gradient(ellipse 90% 60% at 50% -10%, rgba(90, 103, 216, 0.06), transparent 70%), #f8fafc',
          }}
        >
          {mainRoutes}
        </Box>

        {isMobile && (
          <MobileBottomNav
            primaryItems={primaryBottomNavItems}
            onMoreClick={() => setMobileOpen(true)}
          />
        )}
      </Box>
    </Box>
  );
};

export default App;
